import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useAuth, isAuthenticated, role } from "./useAuth"
import { setError, startLoading, stopLoading } from "../components/Notification"
import { getMutex } from "../utils/simpleMutex"
import { EMPTY_GUID } from "../utils/constants"

const _isDirty = ref(0)
const _worker = ref(null)
const _isLoaded = ref(false)

const CurrentWorker = {
  _currentWorker: null,
  _mutex: getMutex(),
  _fatalError: false,
  async getCurrentWorker(fetchAuthAsync, reload) {
    const [lock, release] = this._mutex.getLock()
    await lock
    if (this._currentWorker === null || reload) {
      startLoading("getCurrentWorker")
      try {
        const raw = await fetchAuthAsync(`${AZURE_URI}/Worker/me`, "GET")
        if (raw?.status !== "ok") {
          setError(raw)
        } else if (raw?.result?.id?.length == 36) {
          this._currentWorker = raw.result
        }
      } catch (e) {
        this._fatalError = true
        setError(e)
      }
      stopLoading("getCurrentWorker")
    }
    release()
    return this._currentWorker
  },
}

let _isAuthenticatedWatcherRegistered = false

export function useWorker() {
  const { fetchAuthAsync } = useAuth()

  const loadCurrentWorkerAsync = async (reload = false) => {
    console.log("loadCurrentWorkerAsync")
    try {
      const currentWorker = await CurrentWorker.getCurrentWorker(fetchAuthAsync, reload)
      if (currentWorker?.id?.length == 36) {
        _worker.value = currentWorker
        _isDirty.value += 1
      } else {
        _worker.value = null
      }
      _isLoaded.value = true
    } catch (e) {
      setError(e)
    }
  }

  if (!_isAuthenticatedWatcherRegistered) {
    _isAuthenticatedWatcherRegistered = true
    watch(
      isAuthenticated,
      async (newVal, oldVal) => {
        if (oldVal !== undefined && newVal === oldVal) return
        if (role.value === "Customer") return
        console.log("isAuthenticated changed in useWorker", newVal, oldVal)
        if (newVal) {
          await loadCurrentWorkerAsync()
        }
      },
      { immediate: true },
    )
  }

  return {
    worker: {
      isLoaded: computed(() => _isLoaded.value),
      id: computed(() => _worker.value?.id),
      fullName: computed(() => _worker.value?.fullName),
      workPauseStartDateTime: computed(() => _worker.value?.workPauseStartDateTime),
      isSetupComplete: computed(() => !!_worker.value?.isSetupComplete),
      // the worker's vehicle (Avto), needed for transport entries
      vehicle: computed(() => {
        const w = _worker.value
        return w?.vehicleId && w.vehicleId !== EMPTY_GUID ? { id: w.vehicleId, name: w.vehicleName } : undefined
      }),
      // the worker's material warehouse: their vehicle, or their home warehouse when they have no vehicle
      activeWarehouse: computed(() => {
        const w = _worker.value
        if (w?.vehicleId && w.vehicleId !== EMPTY_GUID) {
          return { id: w.vehicleId, name: w.vehicleName }
        }
        if (w?.homeWarehouseId && w.homeWarehouseId !== EMPTY_GUID) {
          return { id: w.homeWarehouseId, name: w.homeWarehouseName }
        }
        return undefined
      }),
      dirtySignal: computed(() => _isDirty.value),
      canSignLeakageForm: computed(() => _worker.value?.canSignLeakageForm),
      isInventoryManager: computed(() => !!_worker.value?.isInventoryManager),
    },
    loadCurrentWorkerAsync,
  }
}
